import { useState, useEffect } from 'react';
import api from '../services/api';

interface PublicSettings {
  selfRegistrationEnabled: boolean;
  platformName: string;
}

const CACHE_KEY = 'publicSettings';
const CACHE_TTL_MS = 60_000; // Re-validate every 60 seconds
const CACHE_TS_KEY = 'publicSettings_ts';

export function usePublicSettings(): { settings: PublicSettings | null; loading: boolean } {
  const [settings, setSettings] = useState<PublicSettings | null>(() => {
    const cached = sessionStorage.getItem(CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  });
  const [loading, setLoading] = useState(!sessionStorage.getItem(CACHE_KEY));

  useEffect(() => {
    // Check if cache is fresh enough
    const cachedTs = sessionStorage.getItem(CACHE_TS_KEY);
    const isFresh = cachedTs && (Date.now() - Number(cachedTs)) < CACHE_TTL_MS;
    if (settings && isFresh) return;

    let cancelled = false;
    api.get('/auth/public-settings')
      .then((res) => {
        if (cancelled) return;
        const data: PublicSettings = res.data;
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
        sessionStorage.setItem(CACHE_TS_KEY, String(Date.now()));
        setSettings(data);
      })
      .catch(() => {
        if (cancelled) return;
        // Default to disabled on failure
        if (!settings) {
          setSettings({ selfRegistrationEnabled: false, platformName: 'Praxis' });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { settings, loading };
}
